import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { motion } from "framer-motion";
import { BADGE_DEFINITIONS, UserBadge, getBadgeDefinition, getBadgeRarityColor, getBadgesByCategory } from "@/types/badges";

interface AllBadgesModalProps {
  isOpen: boolean;
  onClose: () => void;
  userBadges: UserBadge[];
}

export const AllBadgesModal = ({ isOpen, onClose, userBadges }: AllBadgesModalProps) => {
  const categories = ['activity', 'communication', 'streak', 'service', 'quality', 'community', 'profile', 'progression'] as const;
  
  const renderBadgeCard = (badge: typeof BADGE_DEFINITIONS[0]) => {
    const isEarned = userBadges.some(b => b.badge_slug === badge.slug);
    const earnedBadge = userBadges.find(b => b.badge_slug === badge.slug);
    
    return (
      <motion.div
        key={badge.slug}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.2 }}
        className={`relative p-4 rounded-xl text-center transition-all duration-300 cursor-pointer border-2 ${
          isEarned 
            ? `bg-gradient-to-br ${getBadgeRarityColor(badge.rarity)} border-yellow-300 shadow-lg transform hover:scale-105` 
            : 'bg-gray-800/50 border-gray-600 grayscale opacity-60 hover:opacity-80'
        }`}
        title={earnedBadge ? `Earned on ${new Date(earnedBadge.earned_at).toLocaleDateString()}` : `Requirement: ${badge.requirement}`}
      >
        {/* Rarity indicator */}
        <div className={`absolute top-2 right-2 w-3 h-3 rounded-full ${
          badge.rarity === 'common' ? 'bg-gray-400' :
          badge.rarity === 'uncommon' ? 'bg-green-400' :
          badge.rarity === 'rare' ? 'bg-blue-400' :
          badge.rarity === 'epic' ? 'bg-purple-400' :
          'bg-yellow-400'
        }`} />
        
        <div className="text-4xl mb-3">{badge.icon}</div>
        <h3 className={`font-bold text-sm mb-2 ${isEarned ? 'text-white' : 'text-gray-400'}`}>
          {badge.name}
        </h3>
        <p className={`text-xs mb-2 ${isEarned ? 'text-white/80' : 'text-gray-500'}`}>
          {badge.description}
        </p>
        
        {badge.requirement && (
          <p className={`text-xs mb-2 ${isEarned ? 'text-white/60' : 'text-gray-600'}`}>
            {badge.requirement}
          </p>
        )}
        
        <div className="flex items-center justify-center gap-2">
          <Badge 
            variant={badge.rarity === 'legendary' ? 'default' : 'secondary'}
            className="text-xs capitalize"
          >
            {badge.rarity}
          </Badge>
          {isEarned && (
            <Badge className="bg-green-500 text-white text-xs">
              ✓ Earned
            </Badge>
          )}
        </div>
      </motion.div>
    );
  };

  const earnedCount = userBadges.length;
  const totalCount = BADGE_DEFINITIONS.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden bg-gradient-to-br from-purple-900/95 via-blue-900/95 to-indigo-900/95 backdrop-blur-sm border-purple-500/50">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 text-2xl">
            🏆 All Badges
            <Badge className="ml-auto bg-purple-500 text-white">
              {earnedCount}/{totalCount} earned
            </Badge>
          </DialogTitle>
        </DialogHeader>
        
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid grid-cols-5 lg:grid-cols-9 mb-4">
            <TabsTrigger value="all" className="text-xs">All</TabsTrigger>
            {categories.map(category => (
              <TabsTrigger key={category} value={category} className="text-xs capitalize">
                {category}
              </TabsTrigger>
            ))}
          </TabsList>
          
          <div className="overflow-y-auto max-h-[60vh] pr-2">
            <TabsContent value="all">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {BADGE_DEFINITIONS.map(renderBadgeCard)}
              </div>
            </TabsContent>
            
            {categories.map(category => (
              <TabsContent key={category} value={category}>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {getBadgesByCategory(category).map(renderBadgeCard)}
                </div>
              </TabsContent>
            ))}
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};